import { createFileRoute } from "@tanstack/react-router";
import { lazy, Suspense } from "react";
import packFront from "@/assets/pack-front.png.asset.json";
import { breadcrumbSchema, toolSchema } from "@/lib/schema";
import { snippetFor } from "@/lib/seo-snippets";
import { activeVariant } from "@/lib/seo-ab-titles";

const SNIPPET = snippetFor("/booster-pack")!;
const AB = activeVariant("/booster-pack");

const BoosterPackContent = lazy(() => import("@/components/pages/BoosterPackContent").then((m) => ({ default: m.BoosterPackContent })));

const CARDS_PER_PACK = 7;
const PACKS_PER_BOX = 12;
const DAILY_FREE_PACKS = 12;
const FAQ = [
  {
    q: "What is in a Palworld booster pack?",
    a: `Booster Pack "Dawn of Palpagos" (EBP01) holds ${CARDS_PER_PACK} cards: four Commons, two Uncommons and one hit slot that is always R or better. A booster box contains ${PACKS_PER_BOX} packs — ${CARDS_PER_PACK * PACKS_PER_BOX} cards in total.`,
  },
  {
    q: "How do I open a Pal Stars booster pack?",
    a: `Open the pack opening game, tap the pack to tear it, then flip through all ${CARDS_PER_PACK} cards. Guests get one free demo pack, and a free Pal Stars account unlocks ${DAILY_FREE_PACKS} openings every day.`,
  },
  {
    q: "What are the Palworld booster pack pull rates?",
    a: "The Palworld Official Card Game Dawn of Palpagos pull rates and Palworld booster pack simulator pull rates use a hit slot that resolves to R 60.8%, RR 20%, SR 10%, SP 5%, OSR 2.5%, SSP 1.2% and SSS 0.5%.",
  },
  {
    q: "Are the booster pack cards graded?",
    a: "Yes. Every pull is graded on centering, corners, edges and surface with listed defects, so a flawless Gem Mint 10 stays rare at roughly 1.2% of pulls.",
  },
  {
    q: "Do booster pack cards go into my binder?",
    a: "Signed-in members file any pull into a 108-pocket collector binder, hold it in the trade stack for future trades, or discard it.",
  },
  {
    q: "How to open Pal Stars booster packs online?",
    a: "Open the pack opening page, choose Booster Pack Dawn of Palpagos or a trial deck, tear the wrapper, then flip the seven cards one by one. Account packs and prizes are saved automatically.",
  },
  {
    q: "Palworld booster pack card grading Gem Mint 10 defects?",
    a: "Every pulled card gets centering, corners, edges and surface sub-grades. Gem Mint 10 means no visible defects across the grading report; lower grades list the exact print or handling issue.",
  },
];


function BoosterPackPending() {
  return (
    <div className="mx-auto max-w-5xl px-4 sm:px-6 py-10 space-y-10">
      <header>
        <p className="text-xs uppercase tracking-widest text-primary">Palworld OFFICIAL CARD GAME · EBP01</p>
        <h1 className="mt-2 text-3xl sm:text-4xl font-bold">The Palworld booster pack, explained</h1>
        <p className="aeo-answer mt-3 max-w-3xl text-sm text-muted-foreground">A Palworld Official Card Game booster pack from Booster Pack "Dawn of Palpagos" contains 7 cards, published pull rates and optional Pal Stars grading.</p>
      </header>
      <section className="glass rounded-2xl p-5 sm:p-6">
        <h2 className="text-xl font-bold">Frequently asked questions</h2>
        <dl className="mt-4 space-y-4">
          {FAQ.map((f) => (
            <div key={f.q}>
              <dt className="font-semibold">{f.q}</dt>
              <dd className="aeo-answer mt-1 text-sm text-muted-foreground">{f.a}</dd>
            </div>
          ))}
        </dl>
      </section>
    </div>
  );
}

function RouteComponent() {
  return <Suspense fallback={<BoosterPackPending />}><BoosterPackContent /></Suspense>;
}

export const Route = createFileRoute("/booster-pack")({
  pendingComponent: BoosterPackPending,
  component: RouteComponent,
  head: () => ({
    // Title/description come from the CTR override map: GSC shows 100+ impressions
    // at an average position of ~9.5 with a 0.7% CTR, so the snippet now leads with
    // the "opening simulator" framing and the free daily packs to match searcher intent.
    meta: [
      // Title/description are under a weekly A/B test (see seo-ab-titles.ts).
      { title: AB?.title ?? SNIPPET.title },
      { name: "description", content: AB?.description ?? SNIPPET.description },
      { property: "og:title", content: SNIPPET.social?.title ?? SNIPPET.title },
      { property: "og:description", content: SNIPPET.social?.description ?? SNIPPET.description },
      { property: "og:url", content: "https://palstars.com/booster-pack" },
      { property: "og:type", content: "article" },
      { property: "og:image", content: `https://palstars.com${packFront.url}` },
      { property: "og:image:alt", content: "Palworld Dawn of Palpagos booster pack" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: SNIPPET.social?.title ?? SNIPPET.title },
      { name: "twitter:description", content: SNIPPET.social?.description ?? SNIPPET.description },
      { name: "twitter:image", content: `https://palstars.com${packFront.url}` },
      { name: "robots", content: "index, follow, max-snippet:-1, max-image-preview:large" },
    ],
    links: [{ rel: "canonical", href: "https://palstars.com/booster-pack" }],
    scripts: [
      breadcrumbSchema([["Home", "/"], ["Booster Packs", "/booster-pack"]]),
      // The page is also a free in-browser pack simulator — declaring that as a
      // WebApplication is what lets Google show it as a tool rather than prose.
      toolSchema({
        name: "Palworld Booster Pack Simulator",
        description: `Open Dawn of Palpagos booster packs in the browser with the official pull rates and card grading — ${DAILY_FREE_PACKS} free packs a day.`,
        path: "/booster-pack",
      }),
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          speakable: { "@type": "SpeakableSpecification", cssSelector: ["h1", "h2", ".aeo-answer"] },
          mainEntity: FAQ.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
});
